import * as React from "react";
import { userHasPermission } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase SC-2c — single source of truth for the IA-side owner-payroll gate.
 *
 * Mirrors `useCanManageAccounting` (RBAC-only, `useAuth`-pure, unit-testable).
 * Payroll is deliberately admin-ceiling: monthly pay runs are owner-finance
 * data, not colleague-directory data. The BE re-checks `view_payroll`
 * (reads) / `manage_payroll` (writes) on every call, so this hook is a UX
 * layer, not the only line of defence.
 *
 * Returns:
 *   - `canViewPayroll`   — read-eligibility (runs list, run detail, summary).
 *   - `canManagePayroll` — write-eligibility (generate / edit / finalize).
 *   - `missingView`      — logged in but lacks even `view_payroll`.
 */
export interface PayrollAuthoringGate {
  canViewPayroll: boolean;
  canManagePayroll: boolean;
  missingView: boolean;
}

export function useCanManagePayroll(): PayrollAuthoringGate {
  const { user } = useAuth();
  return React.useMemo<PayrollAuthoringGate>(() => {
    if (!user) {
      return { canViewPayroll: false, canManagePayroll: false, missingView: false };
    }
    const overrides = user.permission_overrides ?? [];
    const canView = userHasPermission(
      user.role,
      overrides,
      "view_payroll",
      user.permission_mode ?? "additive",
    );
    const canManage = userHasPermission(
      user.role,
      overrides,
      "manage_payroll",
      user.permission_mode ?? "additive",
    );
    return {
      canViewPayroll: canView,
      canManagePayroll: canManage,
      missingView: !canView,
    };
  }, [user]);
}
